import type { LoginResult } from '../net/codec/login';
import type { MainMenuState } from '../net/codec/lobby';

/**
 * Mirrors the Unity client's static GameBase: the single source of truth for "who am I" and the
 * lobby-level state every scene reads. Scenes subscribe() and re-render on change.
 */
export interface AppState {
  connected: boolean;
  userId: bigint;
  username: string;
  avatar: number;
  balance: bigint;
  onlineCount: number;
  /** True when the player already has a Wait-state table (gates the Create button). */
  isWaiting: boolean;
  reconnectToken: string;
  adminMessage: string;
}

type Listener = (state: Readonly<AppState>) => void;

const INITIAL: AppState = {
  connected: false,
  userId: 0n,
  username: '',
  avatar: 0,
  balance: 0n,
  onlineCount: 0,
  isWaiting: false,
  reconnectToken: '',
  adminMessage: '',
};

class GameStateStore {
  private state: AppState = { ...INITIAL };
  private readonly listeners = new Set<Listener>();

  get(): Readonly<AppState> {
    return this.state;
  }

  /** Subscribe to state changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  update(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of [...this.listeners]) l(this.state);
  }

  applyLogin(result: LoginResult): void {
    this.update({
      connected: true,
      userId: result.player.userId,
      username: result.player.username,
      avatar: result.player.avatar,
      balance: result.balance,
      onlineCount: result.onlineCount,
      reconnectToken: result.reconnectToken,
    });
  }

  applyMainMenu(menu: MainMenuState): void {
    this.update({
      onlineCount: menu.onlineCount,
      balance: menu.balance,
      isWaiting: menu.isWaiting,
      avatar: menu.avatar,
      adminMessage: menu.adminMessage,
    });
  }

  reset(): void {
    this.update({ ...INITIAL });
  }
}

export const gameState = new GameStateStore();
